import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Home, Globe, Coins, Palette, Briefcase, Phone, ArrowRight } from 'lucide-react';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { name: 'Ana Sayfa', path: '/', icon: Home },
    { name: 'Website Hizmetleri', path: '/website-hizmetleri', icon: Globe },
    { name: 'Kripto Hizmetleri', path: '/kripto-hizmetleri', icon: Coins },
    { name: 'Tasarım Hizmetleri', path: '/tasarim-hizmetleri', icon: Palette },
    { name: 'Portfolyo', path: '/portfolyo', icon: Briefcase },
    { name: 'İletişim', path: '/iletisim', icon: Phone }
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled
        ? 'glass-card shadow-xl py-3 dark:bg-slate-900/80 dark:border-b dark:border-slate-700'
        : 'bg-white/80 dark:bg-slate-900/60 backdrop-blur-md py-4'
        }`}
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center group">
            <img
              src="/viral-logo-2026.png?v=8"
              alt="Viral Strateji"
              className={`transition-all duration-300 ${isScrolled ? '!h-[26px] md:!h-8' : '!h-[33px] md:!h-10'
                } group-hover:scale-110 group-hover:rotate-3`}
              style={{
                filter: 'drop-shadow(0 0 8px rgba(139, 92, 246, 0.4))'
              }}
              onMouseEnter={(e) => e.target.style.filter = 'drop-shadow(0 0 12px rgba(139, 92, 246, 0.4))'}
              onMouseLeave={(e) => e.target.style.filter = 'drop-shadow(0 0 6px rgba(139, 92, 246, 0))'}
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-1">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.name}
                  to={item.path}
                  className={`group relative flex items-center space-x-2 px-4 py-2 rounded-xl text-sm font-medium transition-all duration-300 ${isActive
                    ? 'text-purple-700'
                    : 'text-gray-600 hover:text-purple-700'
                    }`}
                >
                  <Icon className={`w-4 h-4 transition-transform duration-300 group-hover:scale-110 ${isActive ? 'text-purple-600' : ''
                    }`} />
                  <span>{item.name}</span>

                  {/* Active indicator */}
                  {isActive && (
                    <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-1/2 h-0.5 bg-gradient-to-r from-purple-600 to-pink-600 rounded-full" />
                  )}

                  {/* Hover effect */}
                  <div className="absolute inset-0 bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 -z-10" />
                </Link>
              );
            })}
          </nav>

          {/* CTA Button - Desktop */}
          <Link
            to="/iletisim"
            className="hidden lg:flex items-center space-x-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white px-5 py-2.5 rounded-full font-semibold text-sm hover:shadow-lg hover:shadow-purple-500/25 transition-all duration-300 transform hover:scale-105"
          >
            <span>Teklif Al</span>
            <div className="w-5 h-5 bg-white/20 rounded-full flex items-center justify-center">
              <ArrowRight className="w-3 h-3" />
            </div>
          </Link>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="lg:hidden relative p-2 rounded-xl text-gray-600 hover:text-purple-700 hover:bg-purple-50 transition-all duration-300 group"
          >
            <div className="relative w-6 h-6">
              <Menu
                className={`absolute inset-0 w-6 h-6 transition-all duration-300 ${isMenuOpen ? 'opacity-0 rotate-90 scale-0' : 'opacity-100 rotate-0 scale-100'
                  }`}
              />
              <X
                className={`absolute inset-0 w-6 h-6 transition-all duration-300 ${isMenuOpen ? 'opacity-100 rotate-0 scale-100' : 'opacity-0 -rotate-90 scale-0'
                  }`}
              />
            </div>
          </button>
        </div>

        {/* Mobile Navigation */}
        <div
          className={`lg:hidden overflow-hidden transition-all duration-300 ${isMenuOpen ? 'max-h-[600px] opacity-100 mt-4' : 'max-h-0 opacity-0'
            }`}
        >
          <nav className="flex flex-col space-y-2 py-4 border-t border-purple-100">
            {navItems.map((item, index) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.name}
                  to={item.path}
                  onClick={() => setIsMenuOpen(false)}
                  className={`group flex items-center space-x-3 px-4 py-3 rounded-xl text-sm font-medium transition-all duration-300 animate-fade-in-down ${isActive
                    ? 'bg-gradient-to-r from-purple-50 to-pink-50 text-purple-700'
                    : 'text-gray-600 hover:text-purple-700 hover:bg-purple-50'
                    }`}
                  style={{ animationDelay: `${index * 50}ms` }}
                >
                  <div className={`p-2 rounded-lg transition-all duration-300 ${isActive
                    ? 'bg-gradient-to-r from-purple-600 to-pink-600'
                    : 'bg-gray-100 group-hover:bg-gradient-to-r group-hover:from-purple-600 group-hover:to-pink-600'
                    }`}>
                    <Icon className={`w-4 h-4 ${isActive ? 'text-white' : 'text-gray-600 group-hover:text-white'}`} />
                  </div>
                  <span className="flex-1">{item.name}</span>
                  {isActive && (
                    <div className="w-2 h-2 rounded-full bg-gradient-to-r from-purple-600 to-pink-600 animate-pulse" />
                  )}
                </Link>
              );
            })}

            {/* Mobile CTA */}
            <Link
              to="/iletisim"
              onClick={() => setIsMenuOpen(false)}
              className="relative overflow-hidden bg-gradient-to-r from-purple-600 to-pink-600 text-white px-5 py-2.5 rounded-full font-semibold text-center text-sm mt-4 group animate-fade-in-up shadow-lg shadow-purple-500/20"
              style={{ animationDelay: `${navItems.length * 50}ms` }}
            >
              <span className="relative z-10">Teklif Al</span>
              <div className="absolute inset-0 bg-gradient-to-r from-pink-600 to-purple-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            </Link>
          </nav>
        </div>
      </div>
    </header>
  );
};

export default Header;